import { Grid, TextField, Typography } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { Button, StandardDialog } from '@roc/ui';
import { NewTaskFormValues, UnclassifiedDocument } from './unclassifiedDocumentsTypes';

/** "PM_Resume_Smith_Properties.pdf" -> "PM Resume Smith Properties" */
const titleFromFileName = (fileName: string) =>
  (fileName ?? '').replace(/\.[^.]+$/, '').replace(/[_]+/g, ' ').trim();

export interface NewTaskForDocumentDialogProps {
  /** The document the new task is for; null closes the dialog. */
  document: UnclassifiedDocument | null;
  sectionName: string;
  saving: boolean;
  onClose: () => void;
  onCreate: (values: NewTaskFormValues) => void;
}

export const NewTaskForDocumentDialog = ({
  document,
  sectionName,
  saving,
  onClose,
  onCreate,
}: NewTaskForDocumentDialogProps) => {
  const [title, setTitle] = useState('');

  useEffect(() => {
    if (!document) return;
    setTitle(titleFromFileName(document.fileName));
  }, [document]);

  const trimmedTitle = title.trim();

  const dialogContent = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          A new task will be created in <b>{sectionName}</b> and <b>{document?.fileName}</b> will be
          uploaded to it.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Task name"
          variant="outlined"
          size="small"
          fullWidth
          required
          autoFocus
          value={title}
          onChange={e => setTitle(e.target.value)}
          error={!trimmedTitle}
          inputProps={{ maxLength: 255 }}
        />
      </Grid>
    </Grid>
  );

  const dialogActions = (
    <>
      <Button
        style={{ marginRight: 8 }}
        onClick={onClose}
        color="primary"
        variant="outlined"
        disabled={saving}
        testId="new-task-cancel"
      >
        Cancel
      </Button>
      <Button
        onClick={() => onCreate({ title: trimmedTitle })}
        color="primary"
        variant="contained"
        disabled={saving || !trimmedTitle}
        testId="new-task-create"
      >
        Create &amp; Save
      </Button>
    </>
  );

  return (
    <StandardDialog
      open={!!document}
      title="New Task"
      maxWidth="sm"
      handleClose={onClose}
      dialogContent={dialogContent}
      dialogActions={dialogActions}
    />
  );
};
